"use client";

import { useEffect, useMemo, useState, createContext, useContext } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
    Button,
    Label,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTrigger,
} from "@relume_io/relume-ui";
import { BiCookie } from "react-icons/bi";
import { RxCross2 } from "react-icons/rx";
import { loadGoogleAdsTag } from "@/lib/google-ads";

/* =========================================================
   KONFIG – PODMIEŃ NA SWOJE ID
   ========================================================= */
const META_PIXEL_ID = "";  // np. "1234567890"

/* =========================================================
   USTAWIENIA WYŚWIETLANIA BANNERA
   ========================================================= */
type BannerTrigger = "immediate" | "after-scroll" | "scroll-or-delay";
const BANNER_TRIGGER: BannerTrigger = "scroll-or-delay";
const BANNER_DELAY_MS = 8000;     // kiedy trigger = "scroll-or-delay": opóźnienie (ms)
const SCROLL_THRESHOLD_PX = 8;    // ile pikseli ruchu uznajemy za „ruszył w dół”

/* =========================================================
   TYPY I STAŁE
   ========================================================= */
type Category = "marketing" | "personalization" | "analytics";
type CookieConsent = {
    version: string;
    essential: true;
    marketing: boolean;
    personalization: boolean;
    analytics: boolean;
    timestamp: number;
};

const CONSENT_VERSION = "1.1"; // Podniesiono wersję dla nowych ustawień Consent Mode
const LS_KEY = "cookieConsent";
const COOKIE_NAME = "consent";

/* =========================================================
   POMOCNICZE
   ========================================================= */
const getDefaultConsent = (): CookieConsent => ({
    version: CONSENT_VERSION,
    essential: true,
    marketing: false,
    personalization: false,
    analytics: false,
    timestamp: Date.now(),
});

const validateConsent = (value: unknown): value is CookieConsent => {
    return (
        typeof value === "object" &&
        value !== null &&
        (value as any).version === CONSENT_VERSION
    );
};

const setCookie = (name: string, value: string, days = 180) => {
    const maxAge = 60 * 60 * 24 * days;
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax; Secure`;
};

// Funkcja pomocnicza do Google Consent Mode
const updateGtagConsent = (consent: CookieConsent) => {
    if (typeof window !== "undefined" && (window as any).gtag) {
        (window as any).gtag("consent", "update", {
            ad_storage: consent.marketing ? "granted" : "denied",
            ad_user_data: consent.marketing ? "granted" : "denied",
            ad_personalization: consent.marketing ? "granted" : "denied",
            analytics_storage: consent.analytics ? "granted" : "denied",
        });
    }
};

/* =========================================================
   MENEDŻER SDK
   ========================================================= */
const enableGoogleAds = (consent: CookieConsent) => {
    if (!consent.marketing) return;
    loadGoogleAdsTag();
};

const enableMarketing = (consent: CookieConsent) => {
    if (!META_PIXEL_ID) return;
    if (!(window as any).fbq) {
        (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
            if (f.fbq) return;
            n = f.fbq = function () {
                n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
            t = b.createElement(e); t.async = !0; t.src = v;
            s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
        })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
        (window as any).fbq("init", META_PIXEL_ID);
        (window as any).fbq("track", "PageView");
    }
};

const applyCookieSettings = (c: CookieConsent) => {
    if (typeof window === "undefined") return;
    updateGtagConsent(c);
    enableGoogleAds(c);
    enableMarketing(c);
};

/* =========================================================
   KONTEKST
   ========================================================= */
type ConsentContextValue = {
    consent: CookieConsent;
    can: (cat: Category) => boolean;
    openPreferences: () => void;
};
const ConsentContext = createContext<ConsentContextValue | null>(null);
export const useConsent = () => {
    const ctx = useContext(ConsentContext);
    if (!ctx) throw new Error("useConsent must be used within <CookieConsent />");
    return ctx;
};

/* =========================================================
   GŁÓWNY KOMPONENT
   ========================================================= */
export default function CookieConsent({ children }: { children?: React.ReactNode }) {
    const [isMounted, setIsMounted] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [bannerVisible, setBannerVisible] = useState(false);
    const [showPreferencesButton, setShowPreferencesButton] = useState(false);
    const [consent, setConsent] = useState<CookieConsent>(getDefaultConsent());

    useEffect(() => {
        setIsMounted(true);
        const raw = localStorage.getItem(LS_KEY);
        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                if (validateConsent(parsed)) {
                    setConsent(parsed);
                    applyCookieSettings(parsed);
                    setShowPreferencesButton(true);
                    return;
                }
            } catch {
                localStorage.removeItem(LS_KEY);
            }
        }

        // --- POKAZYWANIE BANNERA wg triggera ---
        let timeoutId: number | null = null;
        let shown = false;

        const show = () => {
            if (shown) return;
            shown = true;
            setBannerVisible(true);
            cleanup();
        };

        const onScroll = () => {
            if (window.scrollY >= SCROLL_THRESHOLD_PX) show();
        };

        const onWheelOrTouch = () => show();

        const attachScrollListeners = () => {
            window.addEventListener("scroll", onScroll, { passive: true });
            window.addEventListener("wheel", onWheelOrTouch, { passive: true });
            window.addEventListener("touchmove", onWheelOrTouch, { passive: true });
        };

        const detachScrollListeners = () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("wheel", onWheelOrTouch);
            window.removeEventListener("touchmove", onWheelOrTouch);
        };

        const cleanup = () => {
            if (timeoutId) window.clearTimeout(timeoutId);
            detachScrollListeners();
        };

        switch (BANNER_TRIGGER) {
            case "immediate":
                show();
                break;
            case "after-scroll":
                attachScrollListeners();
                break;
            case "scroll-or-delay":
            default:
                attachScrollListeners();
                timeoutId = window.setTimeout(show, BANNER_DELAY_MS);
                break;
        }

        return cleanup;
    }, []);

    const persistAndApply = (next: CookieConsent) => {
        localStorage.setItem(LS_KEY, JSON.stringify(next));
        setCookie(COOKIE_NAME, JSON.stringify(next));
        setConsent(next);
        applyCookieSettings(next);
        setBannerVisible(false);
        setShowPreferencesButton(true);
        setDialogOpen(false);
    };

    const handleAcceptAll = () => {
        persistAndApply({
            ...getDefaultConsent(),
            marketing: true,
            personalization: true,
            analytics: true,
        });
    };

    const handleRejectAll = () => {
        persistAndApply(getDefaultConsent());
    };

    const ctxValue = useMemo(
        () => ({
            consent,
            can: (cat: Category) => Boolean(consent[cat]),
            openPreferences: () => setDialogOpen(true),
        }),
        [consent]
    );

    return (
        <ConsentContext.Provider value={ctxValue}>
            {children}
            {isMounted ? (
            <section aria-label="Cookie consent manager">
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <AnimatePresence>
                        {showPreferencesButton && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="fixed bottom-4 left-4 z-[1000] md:bottom-8 md:left-8"
                            >
                                <DialogTrigger asChild>
                                    <Button
                                        variant="link"
                                        size="link"
                                        aria-label="Ustawienia prywatności"
                                        className="p-0 m-0 bg-transparent !bg-transparent shadow-none !shadow-none border-0 !border-0 hover:bg-transparent focus:outline-none"
                                    >
                                        <BiCookie className="h-6 w-6 text-current" />
                                    </Button>
                                </DialogTrigger>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <DialogContent className="max-w-lg bg-primary p-6 md:p-8 rounded-3xl border-none shadow-2xl">
                        <h2 className="text-2xl font-bold mb-2">Twoja prywatność</h2>
                        <p className="text-sm text-neutral-600 mb-6">
                            Dostosuj, jakich technologii używamy do ulepszania Twojego doświadczenia.
                        </p>

                        <div className="space-y-6">
                            <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-2xl">
                                <div>
                                    <Label className="font-medium text-lg">Niezbędne</Label>
                                    <p className="text-xs text-neutral-800">Konieczne do działania strony.</p>
                                </div>
                                <span className="text-xs font-medium uppercase text-neutral-600">Zawsze włączone</span>
                            </div>

                            {[
                                { id: "analytics", label: "Analityka", desc: "Pomaga nam ulepszać stronę." },
                                { id: "marketing", label: "Marketing", desc: "Reklamy dopasowane do Ciebie." },
                                { id: "personalization", label: "Personalizacja", desc: "Zapamiętywanie Twoich ustawień." },
                            ].map((cat) => (
                                <div
                                    key={cat.id}
                                    className="flex items-center justify-between p-4 border border-neutral-100 rounded-2xl"
                                >
                                    <div>
                                        <Label htmlFor={cat.id} className="font-medium text-lg cursor-pointer">
                                            {cat.label}
                                        </Label>
                                        <p className="text-xs text-neutral-600">{cat.desc}</p>
                                    </div>
                                    <Checkbox
                                        className="rounded-md"
                                        id={cat.id}
                                        checked={(consent as any)[cat.id]}
                                        onCheckedChange={(checked) =>
                                            setConsent((prev) => ({ ...prev, [cat.id]: !!checked }))
                                        }
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 flex flex-col gap-3">
                            <Button onClick={() => persistAndApply(consent)} className="w-full h-12 rounded-xl">
                                Zapisz preferencje
                            </Button>
                            <Button onClick={handleAcceptAll} variant="secondary" className="w-full h-12 rounded-xl">
                                Akceptuję wszystkie
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

                <AnimatePresence>
                    {bannerVisible && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 50 }}
                            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-8 z-[999] max-w-xl bg-secondary border border-primary-light text-primary shadow-2xl rounded-3xl p-6 md:p-8"
                        >
                            <div className="relative">
                                <button
                                    onClick={() => setBannerVisible(false)}
                                    className="absolute -top-2 -right-2 p-1 text-neutral-400 hover:text-accent"
                                >
                                    <RxCross2 className="size-5" />
                                </button>
                                <p className="text-sm md:text-base leading-relaxed mb-6 pr-6">
                                    Używamy plików cookie, aby zapewnić najlepszą jakość korzystania z naszej witryny.
                                    Możesz zaakceptować wszystkie lub dostosować ustawienia.{" "}
                                    <a
                                        href="/polityka-prywatnosci-i-pliki-cookies"
                                        className="font-semibold underline hover:text-accent"
                                    >
                                        Dowiedz się więcej
                                    </a>
                                    .
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    <Button onClick={handleAcceptAll} className="px-8 h-11 rounded-xl hover:scale-105 duration-500 ease-in-out">
                                        Akceptuję
                                    </Button>
                                    <Button
                                        onClick={handleRejectAll}
                                        variant="secondary"
                                        className="px-8 h-11 hover:scale-105 duration-500 ease-in-out rounded-xl text-neutral-600"
                                    >
                                        Odrzucam
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setBannerVisible(false);
                                            setDialogOpen(true);
                                        }}
                                        variant="link"
                                        className="text-sm font-semibold hover:text-accent text-primary "
                                    >
                                        Ustawienia
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </section>
            ) : null}
        </ConsentContext.Provider>
    );
}